/**
 * Honest bank-wide state summary.
 *
 * The State tab renders the same `StateCard` twice: once titled "Bank" and
 * once per lift. Both used to be fed a `SocketMessageInterface`, and for the
 * bank card that message is `byBank[bankKey]` — which the reducer sets to
 * `{ ...lastLiftMessage, connected, hallCalls }`. Only `connected` and
 * `hallCalls` are genuinely rolled up there (see `rollUpBank`); every other
 * field is whichever car dispatched most recently, wearing the bank's label.
 * A car at floor 4 with its doors open would show as THE BANK's position and
 * THE BANK's doors until a sibling's next heartbeat silently replaced it.
 *
 * This module is the other half of that fix. Rather than widening `rollUpBank`
 * — `byBank` has four other consumers, and blanking `direction`/`position`
 * there would claim "Idle"/"0", a quieter lie than the current one — the bank
 * card reads a separately-typed summary derived from `byLift`, and simply does
 * not render the fields this type deliberately omits.
 *
 * WHAT IS AND IS NOT AGGREGATED, and why:
 *
 * - `faults` — UNION (logical OR). "Any car in the bank is faulted" is a
 *   well-defined predicate and the one an operator actually asks. Reported
 *   alongside `contributorCount` so a car that dropped out of the union is
 *   visible: a fault silently vanishing because its car lost comms is the
 *   worst failure this panel could have.
 * - `frontDoorsOpen` / `rearDoorsOpen` — COUNT. A union with cardinality, not
 *   an invented value. "1 of 3 open" cannot be misread the way a bare
 *   "Open" can.
 * - `serviceModes` — UNION of the distinct modes present, each carrying how
 *   many contributors report it. Without the per-mode count, one car in Fire
 *   Service among two silent siblings renders as a bare "Fire Service" tag
 *   that implies the whole bank.
 * - `direction` — ABSENT BY DESIGN. A bank has no direction; its cars travel
 *   opposite ways at the same time. There is no union (a value cannot be Up
 *   and Down), no useful majority, and a "how many cars are moving" count
 *   would be a different field invented to fill the hole rather than an
 *   aggregate of this one. The per-lift cards show each car's direction.
 * - `position` — ABSENT BY DESIGN, for the same reason. A bank has no floor.
 *
 * CONTRIBUTORS are the bank's CONNECTED lifts, exactly as in `rollUpBank` —
 * one rule to reason about rather than two. A disconnected car is not
 * reporting, so its last-known door and fault state are stale rather than
 * current, and `contributorCount` shrinking is what makes its absence
 * visible on the face of the card.
 */

import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { bankKey, compoundKey, BankKey, LiftKey } from "../../../utils/typedKeys";

/** One service mode present somewhere in the bank, and how many cars report it. */
export interface BankServiceMode {
  mode: string;
  liftCount: number;
}

/**
 * Deliberately NOT a `SocketMessageInterface`. The fields a bank has no
 * honest value for are absent from the type, so a future caller cannot read
 * a per-car value off a bank-labelled object by accident — the way the bank
 * card used to.
 */
export interface BankStateSummary {
  /** Connected lifts of this bank — the denominator for every count below. */
  contributorCount: number;
  /** Contributors whose front door reads open. */
  frontDoorsOpen: number;
  /** Contributors whose rear door reads open. */
  rearDoorsOpen: number;
  /** Contributors reporting an active fault. Zero iff the bank-wide OR is false. */
  faultedCount: number;
  /** Distinct service modes across contributors, sorted for stable rendering. */
  serviceModes: readonly BankServiceMode[];
}

export const EMPTY_BANK_STATE_SUMMARY: BankStateSummary = {
  contributorCount: 0,
  frontDoorsOpen: 0,
  rearDoorsOpen: 0,
  faultedCount: 0,
  serviceModes: [],
};

/**
 * Read one lift's own service mode out of its `serviceMap`.
 *
 * Each dispatched message carries a per-(bank, lift) accumulator, so the map
 * holds this lift's entry alone — but it is keyed by the compound
 * "bankId:liftId", so look it up rather than taking the map's only value.
 * A lift that has never reported a mode contributes nothing.
 */
function serviceModeOf(lift: SocketMessageInterface): string | null {
  return lift.serviceMap[compoundKey(lift.bankId, lift.liftId)] ?? null;
}

/**
 * Derive the bank-wide summary from every lift's CURRENT snapshot.
 *
 * Taken from `byLift` and never from an independently accumulated bank-level
 * value, for the same reason `rollUpBank` does it that way: recomputing from
 * live snapshots is what makes the summary self-healing. When a car clears its
 * fault or closes its doors, the count drops on the next dispatch because the
 * count was never stored anywhere to go stale.
 */
export function summarizeBank(
  byLift: { [key: LiftKey]: SocketMessageInterface },
  bk: BankKey,
): BankStateSummary {
  const modeCounts = new Map<string, number>();
  let contributorCount = 0;
  let frontDoorsOpen = 0;
  let rearDoorsOpen = 0;
  let faultedCount = 0;

  (Object.values(byLift) as SocketMessageInterface[]).forEach((lift) => {
    if (bankKey(lift.bankId) !== bk || !lift.connected) return;
    contributorCount += 1;
    if (lift.doorStatus?.frontOpen) frontDoorsOpen += 1;
    if (lift.doorStatus?.rearOpen) rearDoorsOpen += 1;
    if (lift.faults) faultedCount += 1;
    const mode = serviceModeOf(lift);
    if (mode) modeCounts.set(mode, (modeCounts.get(mode) ?? 0) + 1);
  });

  return {
    contributorCount,
    frontDoorsOpen,
    rearDoorsOpen,
    faultedCount,
    serviceModes: Array.from(modeCounts.entries())
      .map(([mode, liftCount]) => ({ mode, liftCount }))
      .sort((a, b) => a.mode.localeCompare(b.mode)),
  };
}

/**
 * Every service-map entry belonging to this bank, keyed by the compound
 * "bankId:liftId" the Service Map section already renders from.
 *
 * Unlike the counts above this is a LISTING, not a roll-up: every row names
 * its own car, so a disconnected lift's row is attributed rather than
 * misleading, and dropping it would lose information the card used to show.
 * Contributors to the counts are connected-only; rows here are every lift of
 * the bank that has ever reported a mode. The two are different kinds of
 * display and are deliberately scoped differently.
 */
export function bankServiceEntries(
  byLift: { [key: LiftKey]: SocketMessageInterface },
  bk: BankKey,
): { [key: string]: string } {
  const entries: { [key: string]: string } = {};
  (Object.values(byLift) as SocketMessageInterface[]).forEach((lift) => {
    if (bankKey(lift.bankId) !== bk) return;
    const key = compoundKey(lift.bankId, lift.liftId);
    const mode = lift.serviceMap[key];
    if (mode) entries[key] = mode;
  });
  return entries;
}
